import React, { useState } from 'react';
import { Shield, ArrowLeft, KeyRound } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useTheme } from '../ThemeContext';
import logoDark from '../assets/logo_dark.png';
import logoLight from '../assets/logo_light.png';

const LoginPage = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
    const [error, setError] = useState('');
    const [step, setStep] = useState('email'); // 'email' ou 'verification'
    const [countdown, setCountdown] = useState(0);
    const { isDark } = useTheme();

    const startCountdown = () => {
        setCountdown(30);
        const timer = setInterval(() => {
            setCountdown((current) => {
                if (current <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return current - 1;
            });
        }, 1000);
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            console.log('Tentative de vérification email:', email);
            const response = await fetch(`/api/check?email=${encodeURIComponent(email)}`);
            const data = await response.json();
            console.log('Réponse check:', data);

            if (data.message === 'Ok') {
                // Utiliser l'API_URL pour la nouvelle fonctionnalité de vérification
                const verificationResponse = await fetch(`/api/send-verification`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ email }),
                });

                if (verificationResponse.ok) {
                    setStep('verification');
                    startCountdown();
                } else {
                    setError("Erreur lors de l'envoi du code de vérification");
                }
            } else {
                setError("Cet email n'est pas reconnu comme un utilisateur Solganeo.");
            }
        } catch (err) {
            console.error("Erreur:", err);
            setError('Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`/api/verify-code`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    code: verificationCode
                }),
            });

            const data = await response.json();

            if (data.verified) {
                onLogin(data.user);
            } else {
                setError('Code de vérification incorrect');
            }
        } catch (err) {
            setError('Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (countdown > 0) return;

        setIsLoading(true);
        try {
            const response = await fetch(`/api/send-verification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            if (response.ok) {
                startCountdown();
            } else {
                setError("Erreur lors de l'envoi du code");
            }
        } catch (err) {
            setError('Une erreur est survenue');
        } finally {
            setIsLoading(false);
        }
    };

    // ============================================================
    // NOUVEAU : Connexion via passkey (alternative à l'email+OTP)
    // ============================================================
    const handlePasskeyLogin = async () => {
        if (!email) {
            setError("Entrez d'abord votre email pour vous connecter avec un passkey.");
            return;
        }

        setIsPasskeyLoading(true);
        setError('');

        try {
            // Étape 1 : récupérer les options d'authentification pour cet email
            const optionsResponse = await fetch('/api/webauthn/generate-authentication-options', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            if (!optionsResponse.ok) {
                if (optionsResponse.status === 404) {
                    setError("Aucun passkey enregistré pour cet email. Utilisez le code par email, ou enregistrez un passkey après connexion.");
                } else {
                    setError("Impossible de démarrer la connexion par passkey.");
                }
                return;
            }
            const options = await optionsResponse.json();

            // Étape 2 : déclencher la cérémonie WebAuthn (Windows Hello / clé physique / coffre)
            const authenticationResponse = await startAuthentication({ optionsJSON: options });

            // Étape 3 : envoyer la réponse au serveur pour vérification
            const verifyResponse = await fetch('/api/webauthn/verify-authentication', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: authenticationResponse }),
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.verified) {
                onLogin(verifyData.user);
            } else {
                setError(verifyData.message || 'Échec de la connexion par passkey');
            }
        } catch (err) {
            console.error('Erreur connexion passkey:', err);
            if (err.name === 'NotAllowedError') {
                setError('Connexion par passkey annulée ou refusée.');
            } else {
                setError(err.message || 'Une erreur est survenue');
            }
        } finally {
            setIsPasskeyLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
            {/* Logo pour desktop */}
            <img
                src={isDark ? logoDark : logoLight}
                alt="Logo Solganeo"
                className="hidden md:block w-[230px] h-auto mb-8"
            />

            <div className="w-full h-screen md:h-auto md:max-w-md flex flex-col justify-center bg-white dark:bg-gray-800 md:rounded-2xl md:shadow-xl p-8">
                {/* Logo pour mobile */}
                <div className="md:hidden mb-24">
                    <img
                        src={isDark ? logoDark : logoLight}
                        alt="Logo Solganeo"
                        className="w-48 h-auto mx-auto"
                    />
                </div>
                <div className="text-center space-y-6">
                    <div className="flex items-center justify-center gap-2">
                        <Shield className="w-10 h-10 text-blue-500" />
                        <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-400">
                            Authentication
                        </h2>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                        {step === 'email'
                            ? 'Connectez-vous avec votre email Solganeo'
                            : 'Entrez le code de vérification envoyé par email'
                        }
                    </p>
                </div>

                {step === 'email' ? (
                    <form onSubmit={handleEmailSubmit} className="mt-8 space-y-6">
                        <div className="space-y-2">
                            <label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Adresse email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input"
                                placeholder="nom@solganeo.com"
                            />
                        </div>

                        {error && (
                            <div className="p-4 text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-xl">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || isPasskeyLoading}
                            className="w-full btn-primary"
                        >
                            {isLoading ? 'Vérification...' : 'Continuer'}
                        </button>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                            </div>
                            <div className="relative flex justify-center text-xs">
                                <span className="px-2 bg-white dark:bg-gray-800 text-gray-400">ou</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handlePasskeyLogin}
                            disabled={isLoading || isPasskeyLoading}
                            className="w-full btn-secondary"
                        >
                            <KeyRound className="w-5 h-5" />
                            {isPasskeyLoading ? 'Connexion...' : 'Se connecter avec un passkey'}
                        </button>
                    </form>
                ) : (
                    <div className="mt-8 space-y-6">
                        <button
                            onClick={() => setStep('email')}
                            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour
                        </button>

                        <form onSubmit={handleCodeSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <label htmlFor="code" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Code de vérification
                                </label>
                                <input
                                    id="code"
                                    name="code"
                                    type="text"
                                    required
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value)}
                                    className="input"
                                    placeholder="Entrez le code à 6 chiffres"
                                    maxLength={6}
                                />
                            </div>

                            {error && (
                                <div className="p-4 text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-xl">
                                    {error}
                                </div>
                            )}

                            <div className="space-y-4">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full btn-primary"
                                >
                                    {isLoading ? 'Vérification...' : 'Se connecter'}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleResendCode}
                                    disabled={countdown > 0 || isLoading}
                                    className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
                                >
                                    {countdown > 0
                                        ? `Renvoyer le code (${countdown}s)`
                                        : 'Renvoyer le code'
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginPage;